import { FC } from "react";
import { JobPerformanceProjectRun } from "@app/operations/job-performance/jobPerformanceInterfaces";

type Props = {
    run: JobPerformanceProjectRun;
    // 'zip' runs publish synchronously (a DB timestamp, not a background job) so they have no
    // publish queue/run phases to show — 'standard' runs publish via a background job and do.
    variant?: 'standard' | 'zip';
};

const fmt = (v: number | null | undefined): string =>
    v === null || v === undefined ? '—' : v.toFixed(1);

const isNullOrZero = (v: number | null | undefined): boolean =>
    v === null || v === undefined || v === 0;

export const JobPerformanceRunBreakdownPanel: FC<Props> = ({ run, variant = 'standard' }) => {
    if (variant === 'zip') {
        return (
            <div className="jp-run-panel">
                <div className="jp-run-panel__title">
                    End-to-End Timing Breakdown — {run.projectName}
                </div>
                <table className="jp-run-panel__grid">
                    <thead>
                        <tr>
                            <th className="col-phase">Queue Wait</th>
                            <th className="col-phase">Project Run Time</th>
                            <th className="col-total">Total End-to-End</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            <td>{fmt(run.queueWaitMinutes)} min</td>
                            <td>{fmt(run.projectRunTimeMinutes)} min</td>
                            <td className="col-total">{fmt(run.totalEndToEndMinutes)} min</td>
                        </tr>
                    </tbody>
                </table>
                <div className="jp-run-panel__status">
                    {'Publish status: '}
                    <span className="jp-run-panel__status-value">{run.isPublished ? 'Published' : 'Not Published'}</span>
                    {run.isPublished && run.timeToPublishMinutes !== null && (
                        <>{' · '}{fmt(run.timeToPublishMinutes)} min after run finished</>
                    )}
                    {' · '}
                    {run.jobsProcessed.toLocaleString()} child jobs
                </div>
            </div>
        );
    }

    const publishMissing =
        isNullOrZero(run.publishQueueWaitMinutes) && isNullOrZero(run.publishRunTimeMinutes);

    return (
        <div className="jp-run-panel">
            <div className="jp-run-panel__title">
                End-to-End Timing Breakdown — {run.projectName}
            </div>
            <table className="jp-run-panel__grid">
                <thead>
                    <tr>
                        <th className="col-phase">Queue Wait</th>
                        <th className="col-phase">Project Run Time</th>
                        <th className="col-phase">Pub Queue Wait</th>
                        <th className="col-phase">Publish Run</th>
                        <th className="col-total">Total End-to-End</th>
                    </tr>
                </thead>
                <tbody>
                    <tr>
                        <td>{fmt(run.queueWaitMinutes)} min</td>
                        <td>{fmt(run.projectRunTimeMinutes)} min</td>
                        <td className={publishMissing ? 'col-null' : ''}>
                            {publishMissing ? '—' : `${fmt(run.publishQueueWaitMinutes)} min`}
                        </td>
                        <td className={publishMissing ? 'col-null' : ''}>
                            {publishMissing ? '—' : `${fmt(run.publishRunTimeMinutes)} min`}
                        </td>
                        <td className="col-total">{fmt(run.totalEndToEndMinutes)} min</td>
                    </tr>
                </tbody>
            </table>
            <div className="jp-run-panel__status">
                {publishMissing
                    ? 'Publish status: No publish data for this run'
                    : (
                        <>
                            {'Publish status: '}
                            <span className="jp-run-panel__status-value">Complete</span>
                            {' · '}
                            {run.jobsProcessed.toLocaleString()} child jobs
                        </>
                    )
                }
            </div>
        </div>
    );
};
